/**
 * Password sign-in, for Local accounts: the break-glass path at /signin/local
 * that works when Microsoft does not (backend/app/api/routes/auth.py). /signin
 * shows it too when Microsoft sign-in is not configured. Atlas's sign-in card.
 */

import { useState, type ReactNode } from 'react';
import { LogIn, ShieldCheck } from 'lucide-react';

import { useProviders, useSignIn } from '@/api/queries';
import { BrandMark } from '@/shell/BrandMark';
import { FormError } from '@/ui/StateCard';
import { ActionButton, buttonState } from '@/ui/ActionButton';

/** The sign-in card: the brand, a rule, then the content. */
export function AuthCard({ children, onSubmit }: { children: ReactNode; onSubmit?: () => void }) {
  const body = (
    <>
      <div className="auth-logo">
        <span className="auth-logo-mark">
          <BrandMark />
        </span>
        <div className="auth-logo-text">
          <div className="auth-wordmark">Wynxx Discover</div>
          <div className="hint">GFT US · Idea discovery &amp; research</div>
        </div>
      </div>
      <div className="auth-divider" role="presentation" />
      {children}
    </>
  );
  return (
    <div className="auth-page">
      <div className="card auth-card">
        {onSubmit ? (
          <form
            className="card-body auth-signin-body"
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit();
            }}
          >
            {body}
          </form>
        ) : (
          <div className="card-body auth-signin-body">{body}</div>
        )}
      </div>
    </div>
  );
}

/** The one Microsoft button: primary on the cards that offer only Microsoft, secondary under a password form. */
export function MicrosoftButton({ href, secondary = false }: { href: string; secondary?: boolean }) {
  return (
    <a className={`btn ${secondary ? 'btn-secondary' : 'btn-primary'} auth-action`} href={href}>
      <LogIn aria-hidden="true" /> Sign in with Microsoft
    </a>
  );
}

export function PasswordSignIn({ localOnly = false }: { localOnly?: boolean }) {
  const signIn = useSignIn();
  const providers = useProviders();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  return (
    <AuthCard onSubmit={() => signIn.mutate({ email, password })}>
      <div className="stack-md">
        {localOnly && <p className="hint">For local accounts only.</p>}
        <div className="field">
          <label htmlFor="signin-email">E-mail</label>
          <input
            id="signin-email"
            type="email"
            autoComplete="username"
            required
            autoFocus
            value={email}
            placeholder="you@gft.com"
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="signin-password">Password</label>
          <input
            id="signin-password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <FormError error={signIn.error} />

        <ActionButton
          state={buttonState(signIn)}
          className="btn btn-primary auth-action"
          icon={<LogIn aria-hidden="true" />}
          label="Sign in"
          pendingLabel="Signing in…"
          doneLabel="Signed in"
        />
        {localOnly && providers.data?.entra && <MicrosoftButton href="/signin" secondary />}
      </div>

      <p className="hint auth-foot">
        <ShieldCheck aria-hidden="true" />
        Accounts are created by an administrator. There is no self sign-up.
      </p>
    </AuthCard>
  );
}

export function SignInLocal() {
  return <PasswordSignIn localOnly />;
}
